import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AuthBrandHeader } from './AuthBrandHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';

const ActivatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email');
  
  // Use email as username since Cognito is configured with username_attributes = ["email"]
  const [email, setEmail] = useState(emailParam || '');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'form' | 'success'>('form');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { confirmSignup, refreshAuth, user } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Use email as username since Cognito uses email as the username
      await confirmSignup(email, code);
      // Cognito may have signed the user in automatically; refresh and redirect if so
      await refreshAuth();
      setStatus('success');
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  // After success: if Cognito auto-signed in the user go straight to /welcome,
  // otherwise send them to /login with a next param so they land on /welcome after signing in.
  React.useEffect(() => {
    if (status === 'success') {
      if (user) {
        navigate('/welcome', { replace: true });
      } else {
        // Cognito did not auto-sign in — redirect to login and then welcome
        navigate('/login?next=/welcome', { replace: true });
      }
    }
  }, [status, user, navigate]);

  if (status === 'success') {
    // Brief transitional render while the effect above fires
    return null;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background px-4 py-12"
      style={{ backgroundImage: 'var(--surface-pattern)' }}
    >
      <div className="w-full max-w-md">
        <AuthBrandHeader />
        <Card className="border-border bg-card shadow-none">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-foreground">Verify your email</CardTitle>
            <CardDescription>
              Enter the verification code sent to {emailParam || 'your email'}
            </CardDescription>
          </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="Enter your email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Verification code</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                placeholder="Enter 6-digit code"
                maxLength={6}
              />
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button
              type="submit"
              className="w-full h-10"
              disabled={submitting}
            >
              {submitting ? 'Verifying…' : 'Verify email'}
            </Button>
          </form>
          <p className="mt-4 text-sm text-muted-foreground">
            <Link to="/login" className="hover:underline">
              Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default ActivatePage;
